import { create } from "zustand";
import { db } from "../lib/db";
import { ensureSeeded, getVocab, initVocab } from "../lib/seed";
import { applyAnswer } from "../lib/learning";
import {
  pickNext,
  SESSION_COMPLETE,
  nextEarliestIndexAfterCorrect,
  nextEarliestIndexAfterWrong,
  lookupVocab,
  type SchedulerScope,
} from "../lib/scheduling";
import { buildChoices, type Choice } from "../lib/distractors";
import { createRng, type Rng } from "../lib/rng";
import { applyTheme } from "../lib/theme";
import { playCorrect, playWrong } from "../lib/feedback";
import { enqueue } from "../lib/outbox";
import { hydrateFromServer, wipeLocalIfNeeded } from "../lib/hydrate";
import { me as meApi } from "../lib/api";
import { useAuthStore } from "./authStore";
import type {
  Direction,
  LearningDirection,
  LearningMode,
  ProgressRecord,
  SettingsMeta,
  ThemePref,
  VocabEntry,
} from "../lib/types";

/** Set by the sync manager (Stage E) so the store can trigger an outbox drain
 *  after each mutation. Default is a no-op so unit tests don't need to mock. */
let triggerSyncSoon: () => void = () => {};
export function setSyncTrigger(fn: () => void): void {
  triggerSyncSoon = fn;
}

export type Counters = {
  total: number;
  mastered: number;
  inProgress: number;
  wrong: number;
};

export type Feedback =
  | { kind: "none" }
  | { kind: "correct"; correctGerman: string }
  | { kind: "wrong"; correctGerman: string; chosenGerman: string };

export type QuizStatus = "idle" | "loading" | "ready" | "complete" | "error";

export type Settings = {
  theme: ThemePref;
  sound: boolean;
  learningDirection: LearningDirection;
  learningMode: LearningMode;
};

/** A snapshot of a fully-answered question kept in the in-memory history ring. */
export type HistoryEntry = {
  prompt: VocabEntry;
  direction: Direction;
  choices: Choice[];
  chosenGerman: string;
  isCorrect: boolean;
  feedback: Feedback;
};

export const HISTORY_LIMIT = 20;

export type QuizState = {
  status: QuizStatus;
  errorMessage: string | null;
  vocab: VocabEntry[];
  /** Progress-Map mit Compound-Key `${english}|${direction}`. */
  progress: Map<string, ProgressRecord>;
  questionsAnswered: number;
  currentPrompt: VocabEntry | null;
  /** Direction für den aktuell angezeigten Prompt — kann sich vom Settings-Wert
   *  unterscheiden, falls der User mitten in der Frage die Richtung wechselt
   *  (Race-Schutz). recordAnswer schreibt immer in diese Direction. */
  currentDirection: Direction | null;
  currentChoices: Choice[];
  feedback: Feedback;
  counters: Counters;
  settings: Settings;
  /** Englische Wörter, die der User markiert hat (Issue #123). */
  markers: Set<string>;
  /** Most-recent-first list of past answered questions (max HISTORY_LIMIT). */
  history: HistoryEntry[];
  /** `null` = live quiz state, otherwise index into `history` (0 = most recent past). */
  historyIndex: number | null;

  loadAll: () => Promise<void>;
  nextPrompt: () => Promise<void>;
  recordAnswer: (chosenGerman: string) => Promise<void>;
  resetAll: () => Promise<void>;
  setTheme: (theme: ThemePref) => Promise<void>;
  setSound: (on: boolean) => Promise<void>;
  setLearningDirection: (dir: LearningDirection) => Promise<void>;
  setLearningMode: (mode: LearningMode) => Promise<void>;
  toggleMarked: (english: string) => Promise<void>;
  goBack: () => void;
  goForward: () => void;
};

const DEFAULT_SETTINGS: Settings = {
  theme: "system",
  sound: false,
  learningDirection: "en_de",
  learningMode: "all",
};

function progressKey(english: string, direction: Direction): string {
  return `${english}|${direction}`;
}

/**
 * Aggregiert Counters über alle Progress-Rows einer bestimmten Direction-Auswahl.
 *  - `en_de` / `de_en`: nur Rows dieser Direction, total = vocab.length
 *  - `mixed`: kombiniert beide Directions (per-row Aggregation), total = vocab.length * 2
 *
 * Phase 3 kann diese Semantik bei Bedarf verfeinern (z. B. per-word mastered in mixed).
 */
function deriveCounters(
  progress: Map<string, ProgressRecord>,
  vocabSize: number,
  scope: LearningDirection,
): Counters {
  let mastered = 0;
  let inProgress = 0;
  let wrong = 0;
  let totalRows = 0;
  for (const p of progress.values()) {
    if (scope !== "mixed" && p.direction !== scope) continue;
    totalRows++;
    if (p.state === "mastered") mastered++;
    else if (p.state === "in_progress") inProgress++;
    else if (p.state === "wrong") wrong++;
  }
  const total = scope === "mixed" ? vocabSize * 2 : vocabSize;
  // Falls ensureSeeded noch nicht alle Rows angelegt hat, fallback auf totalRows.
  return { total: Math.max(total, totalRows), mastered, inProgress, wrong };
}

async function readSessionAnswered(): Promise<number> {
  const session = await db.meta.get("session");
  return session && session.key === "session" ? session.questionsAnswered : 0;
}

async function readOrCreateSettings(): Promise<SettingsMeta> {
  const existing = await db.meta.get("settings");
  if (existing && existing.key === "settings") return existing;
  const fresh: SettingsMeta = {
    key: "settings",
    rngSeed: Math.floor(Math.random() * 2 ** 31) || 0x9e3779b9,
    reduceMotion: false,
    theme: DEFAULT_SETTINGS.theme,
    sound: DEFAULT_SETTINGS.sound,
    learningDirection: DEFAULT_SETTINGS.learningDirection,
    learningMode: DEFAULT_SETTINGS.learningMode,
  };
  await db.meta.put(fresh);
  return fresh;
}

function pickSettings(meta: SettingsMeta): Settings {
  return {
    theme: meta.theme ?? DEFAULT_SETTINGS.theme,
    sound: meta.sound ?? DEFAULT_SETTINGS.sound,
    learningDirection: meta.learningDirection ?? DEFAULT_SETTINGS.learningDirection,
    learningMode: meta.learningMode ?? DEFAULT_SETTINGS.learningMode,
  };
}

async function loadMarkersFromDb(): Promise<Set<string>> {
  const rows = await db.markers.toArray();
  return new Set(rows.map((r) => r.english));
}

let rng: Rng | null = null;
async function getRng(): Promise<Rng> {
  if (rng) return rng;
  const meta = await readOrCreateSettings();
  rng = createRng(meta.rngSeed);
  return rng;
}

async function patchSettings(patch: Partial<Omit<SettingsMeta, "key">>): Promise<SettingsMeta> {
  const current = await readOrCreateSettings();
  const next: SettingsMeta = { ...current, ...patch };
  await db.meta.put(next);
  return next;
}

export const useQuizStore = create<QuizState>((set, get) => ({
  status: "idle",
  errorMessage: null,
  vocab: [],
  progress: new Map(),
  questionsAnswered: 0,
  currentPrompt: null,
  currentDirection: null,
  currentChoices: [],
  feedback: { kind: "none" },
  counters: { total: 0, mastered: 0, inProgress: 0, wrong: 0 },
  settings: DEFAULT_SETTINGS,
  markers: new Set(),
  history: [],
  historyIndex: null,

  async loadAll() {
    set({ status: "loading", errorMessage: null });
    try {
      if (typeof indexedDB === "undefined") {
        throw new Error(
          "Dein Browser unterstützt kein IndexedDB. Aktiviere die Speicherung oder verlasse den Inkognito-Modus.",
        );
      }
      const authed = useAuthStore.getState().status === "authed";
      if (authed) {
        await wipeLocalIfNeeded();
        try {
          await hydrateFromServer();
        } catch {
          // Offline or server hiccup — proceed with whatever is in IDB.
        }
      }
      await initVocab();
      await ensureSeeded();
      const vocab = getVocab();
      const allProgress = await db.progress.toArray();
      const progressMap = new Map(allProgress.map((p) => [progressKey(p.english, p.direction), p]));
      const questionsAnswered = await readSessionAnswered();
      const meta = await readOrCreateSettings();
      const settings = pickSettings(meta);
      const markers = await loadMarkersFromDb();
      applyTheme(settings.theme);
      set({
        vocab,
        progress: progressMap,
        questionsAnswered,
        counters: deriveCounters(progressMap, vocab.length, settings.learningDirection),
        settings,
        markers,
        status: "ready",
      });
      await get().nextPrompt();
    } catch (err) {
      set({
        status: "error",
        errorMessage: err instanceof Error ? err.message : String(err),
      });
    }
  },

  async nextPrompt() {
    const { progress, vocab, questionsAnswered, settings, markers } = get();
    const r = await getRng();
    const list = Array.from(progress.values());
    const scope: SchedulerScope = settings.learningDirection;
    // Issue #123: im 'marked'-Modus filtert pickNext den Pool auf die markierten
    // Wörter. Wenn keine markiert sind, ist die `pick` === SESSION_COMPLETE →
    // QuizScreen zeigt einen Empty-State (Phase 3).
    const markedOnly = settings.learningMode === "marked" ? markers : undefined;
    const pick = pickNext(list, questionsAnswered, r, scope, markedOnly);
    if (pick === SESSION_COMPLETE) {
      set({
        status: "complete",
        currentPrompt: null,
        currentDirection: null,
        currentChoices: [],
        feedback: { kind: "none" },
      });
      return;
    }
    const target = lookupVocab(vocab, pick.english);
    const choices = buildChoices(target, vocab, r, pick.direction);
    set({
      status: "ready",
      currentPrompt: target,
      currentDirection: pick.direction,
      currentChoices: choices,
      feedback: { kind: "none" },
      historyIndex: null,
    });
  },

  async recordAnswer(chosenGerman: string) {
    // Block writes while viewing history — only "Weiter" returns to live then.
    const liveState = get();
    if (liveState.historyIndex !== null) return;

    const {
      currentPrompt,
      currentDirection,
      currentChoices,
      progress,
      questionsAnswered,
      vocab,
      settings,
      history,
    } = liveState;
    if (!currentPrompt || !currentDirection) return;
    const target = currentPrompt;
    const direction = currentDirection;
    // Korrekte Antwort hängt von der Direction ab: bei DE→EN ist es das
    // englische Wort, bei EN→DE das deutsche. Der `chosenGerman`-Parameter-
    // Name bleibt aus Phase-2-Minimal-Footprint-Gründen.
    const correctValue = direction === "en_de" ? target.german : target.english;
    const isCorrect = chosenGerman === correctValue;

    // iOS Safari rule: audio playback must happen synchronously inside the
    // user gesture, BEFORE the first await.
    if (settings.sound) (isCorrect ? playCorrect : playWrong)();

    const r = await getRng();
    const now = Date.now();
    const key = progressKey(target.english, direction);
    const prev = progress.get(key);
    if (!prev) throw new Error(`progress missing for ${key}`);

    const newAnswered = questionsAnswered + 1;
    const isMarkedMode = settings.learningMode === "marked";

    // Cert-Isolation (Spec 50 §6, Issue #123):
    //   - 'all'-Modus: applyAnswer mutiert state/remainingCorrect/consecutive
    //     wie gewohnt → IDB + Outbox.
    //   - 'marked'-Modus: state/remaining/consecutive bleiben unverändert
    //     (Antworten zählen NICHT fürs Zertifikat). Nur earliestQuestionIndex
    //     wird transient erhöht, damit pickNext den Pool weiterrotiert.
    //     Mutation ist nur in-memory, kein IDB-write, kein Outbox-push.
    let next: ProgressRecord;
    if (isMarkedMode) {
      next = {
        ...prev,
        earliestQuestionIndex: isCorrect
          ? nextEarliestIndexAfterCorrect(newAnswered, prev.consecutiveCorrect)
          : nextEarliestIndexAfterWrong(newAnswered, r),
      };
    } else {
      let advanced = applyAnswer(prev, isCorrect, now);
      advanced = {
        ...advanced,
        earliestQuestionIndex: isCorrect
          ? nextEarliestIndexAfterCorrect(newAnswered, advanced.consecutiveCorrect)
          : nextEarliestIndexAfterWrong(newAnswered, r),
      };
      next = advanced;
    }

    const newProgress = new Map(progress);
    newProgress.set(key, next);

    if (!isMarkedMode) {
      await db.transaction("rw", db.progress, db.meta, async () => {
        await db.progress.put(next);
        const existing = await db.meta.get("session");
        const startedAt = existing && existing.key === "session" ? existing.startedAt : now;
        await db.meta.put({
          key: "session",
          questionsAnswered: newAnswered,
          startedAt,
          totalElapsedMs: existing && existing.key === "session" ? existing.totalElapsedMs : 0,
        });
      });

      if (useAuthStore.getState().status === "authed") {
        await enqueue("answer", {
          english: target.english,
          direction,
          wasCorrect: isCorrect,
          questionIndex: newAnswered - 1,
          ts: new Date(now).toISOString(),
        });
        triggerSyncSoon();
      }
    }

    const feedback: Feedback = isCorrect
      ? { kind: "correct", correctGerman: correctValue }
      : { kind: "wrong", correctGerman: correctValue, chosenGerman };

    // Prepend the new entry, cap at HISTORY_LIMIT.
    const nextHistory: HistoryEntry[] = [
      {
        prompt: target,
        direction,
        choices: currentChoices,
        chosenGerman,
        isCorrect,
        feedback,
      },
      ...history,
    ].slice(0, HISTORY_LIMIT);

    set({
      progress: newProgress,
      questionsAnswered: newAnswered,
      // Counter im marked-Modus unverändert (Cert-Isolation): die im
      // StatsScreen sichtbaren mastered/in-progress-/wrong-Zähler reflektieren
      // weiterhin den Hauptkurs, nicht die transienten Mark-Mode-Antworten.
      counters: isMarkedMode
        ? get().counters
        : deriveCounters(newProgress, vocab.length, settings.learningDirection),
      feedback,
      history: nextHistory,
      historyIndex: null,
    });
  },

  async resetAll() {
    const meta = await readOrCreateSettings();
    rng = null;
    const authed = useAuthStore.getState().status === "authed";
    if (authed) {
      await db.outbox.clear();
      try {
        await meApi.reset();
      } catch {
        await enqueue("reset", {});
      }
    }
    await db.transaction("rw", db.progress, db.meta, db.markers, async () => {
      await db.progress.clear();
      await db.markers.clear();
      await db.meta.clear();
      await db.meta.put({ ...meta, rngSeed: Math.floor(Math.random() * 2 ** 31) || 0x9e3779b9 });
    });
    set({
      status: "idle",
      progress: new Map(),
      questionsAnswered: 0,
      currentPrompt: null,
      currentDirection: null,
      currentChoices: [],
      feedback: { kind: "none" },
      counters: { total: 0, mastered: 0, inProgress: 0, wrong: 0 },
      markers: new Set(),
      history: [],
      historyIndex: null,
    });
    await get().loadAll();
  },

  async setTheme(theme) {
    await patchSettings({ theme });
    applyTheme(theme);
    set({ settings: { ...get().settings, theme } });
    if (useAuthStore.getState().status === "authed") {
      await enqueue("settings", { theme });
      triggerSyncSoon();
    }
  },

  async setSound(on) {
    await patchSettings({ sound: on });
    set({ settings: { ...get().settings, sound: on } });
    if (useAuthStore.getState().status === "authed") {
      await enqueue("settings", { sound: on });
      triggerSyncSoon();
    }
  },

  async setLearningDirection(dir) {
    await patchSettings({ learningDirection: dir });
    const next: Settings = { ...get().settings, learningDirection: dir };
    set({
      settings: next,
      counters: deriveCounters(get().progress, get().vocab.length, dir),
    });
    if (useAuthStore.getState().status === "authed") {
      await enqueue("settings", { learningDirection: dir });
      triggerSyncSoon();
    }
    // Aktueller Prompt verwerfen falls noch unbeantwortet. Sonst (Feedback
    // sichtbar) lassen wir den User die aktuelle Frage zu Ende anschauen
    // und ziehen erst beim nächsten Weiter aus der neuen Direction.
    const { feedback, status } = get();
    if (feedback.kind === "none" && status === "ready") {
      await get().nextPrompt();
    }
  },

  async setLearningMode(mode) {
    await patchSettings({ learningMode: mode });
    set({ settings: { ...get().settings, learningMode: mode } });
    if (useAuthStore.getState().status === "authed") {
      await enqueue("settings", { learningMode: mode });
      triggerSyncSoon();
    }
    // Aktueller Prompt verwerfen falls noch unbeantwortet (analog
    // setLearningDirection). Beim Wechsel ins 'marked'-Mode mit leerem
    // markers-Set zeigt nextPrompt direkt den Empty-State (status=complete).
    // WICHTIG: nextPrompt läuft auch im status='complete', sonst kann der
    // User aus dem Empty-State nicht wieder rauskommen — bei marked→all
    // muss der neu erlaubte Pool sofort einen Prompt liefern.
    const { feedback } = get();
    if (feedback.kind === "none") {
      await get().nextPrompt();
    }
  },

  async toggleMarked(english) {
    const currentMarkers = get().markers;
    const willBeMarked = !currentMarkers.has(english);
    // Optimistic update + transient IDB-write; Server-Push via Outbox.
    const nextMarkers = new Set(currentMarkers);
    if (willBeMarked) nextMarkers.add(english);
    else nextMarkers.delete(english);

    if (willBeMarked) {
      await db.markers.put({ english, createdAt: Date.now() });
    } else {
      await db.markers.delete(english);
    }
    set({ markers: nextMarkers });

    if (useAuthStore.getState().status === "authed") {
      await enqueue("marker", { english, marked: willBeMarked });
      triggerSyncSoon();
    }
  },

  goBack() {
    const { history, historyIndex } = get();
    if (history.length === 0) return;
    if (historyIndex === null) {
      set({ historyIndex: 0 });
      return;
    }
    if (historyIndex < history.length - 1) {
      set({ historyIndex: historyIndex + 1 });
    }
  },

  goForward() {
    const { historyIndex } = get();
    if (historyIndex === null) return;
    if (historyIndex === 0) {
      set({ historyIndex: null });
      return;
    }
    set({ historyIndex: historyIndex - 1 });
  },
}));
